import type { RouteRecordRaw, RouteRecordRedirectOption } from 'vue-router';

import {
  ADD_ONS_PERMISSION,
  canAccessSettings,
  CLIENT_SEGMENTS_PERMISSION,
  COMPANY_SETTINGS_PERMISSIONS,
  LOCATION_SETTINGS_PERMISSIONS,
  OrganizationModule,
  useAuthStore,
} from '@/entities/auth';

const EmployeesPage = () => import('@/pages/employees').then((m) => ({ default: m.EmployeesPage }));
const SettingsHomePage = () => import('@/pages/settings/settings-home').then((m) => ({ default: m.SettingsHomePage }));
const BusinessSettingsPage = () =>
  import('@/pages/settings/business-settings').then((m) => ({ default: m.BusinessSettingsPage }));
const ServicesSettingsPage = () =>
  import('@/pages/settings/services').then((m) => ({ default: m.ServicesSettingsPage }));
const TireServiceSettingsPage = () =>
  import('@/pages/settings/tire-service-settings').then((m) => ({ default: m.TireServiceSettingsPage }));
const ClientSegmentsPage = () =>
  import('@/pages/settings/client-settings').then((m) => ({ default: m.ClientSegmentsPage }));
const LoyaltySettingsPage = () =>
  import('@/pages/settings/loyalty-settings').then((m) => ({ default: m.LoyaltySettingsPage }));
const AddOnsPage = () => import('@/pages/add-ons').then((m) => ({ default: m.AddOnsPage }));
const RobokassaAddOnPage = () => import('@/pages/add-ons').then((m) => ({ default: m.RobokassaAddOnPage }));
const TireServiceAddOnPage = () => import('@/pages/add-ons').then((m) => ({ default: m.TireServiceAddOnPage }));
const RadioAddOnPage = () => import('@/pages/add-ons').then((m) => ({ default: m.RadioAddOnPage }));
const InvoicesAddOnPage = () => import('@/pages/add-ons').then((m) => ({ default: m.InvoicesAddOnPage }));

const clientSegmentsRouteMeta = {
  navigationSection: 'settings',
  requiresAuth: true,
  requiresOrganization: true,
  title: 'Сегменты клиентов',
  modules: [OrganizationModule.Clients],
  permissions: [CLIENT_SEGMENTS_PERMISSION],
};
const addOnsRouteMeta = {
  navigationSection: 'settings',
  requiresAuth: true,
  requiresOrganization: true,
  permissions: [ADD_ONS_PERMISSION],
};

/** Раздел открыт и при выключенном хранении: на нём хранение и включают. */
const tireServiceSettingsRouteMeta = {
  navigationSection: 'settings',
  requiresAuth: true,
  requiresOrganization: true,
  locationOptional: true,
  modules: [OrganizationModule.TireService],
  permissions: ['tire_service.settings'],
};

/** Старые URL настроек ведут в секцию рабочей локации, а без неё — в список локаций. */
const workingLocationSection =
  (section: string): RouteRecordRedirectOption =>
  () => {
    const locationId = useAuthStore().currentLocation?.id;
    return locationId
      ? { name: 'business-settings.locations.detail', params: { locationId, section } }
      : { name: 'business-settings.locations' };
  };

export const settingsRoutes: RouteRecordRaw[] = [
  {
    path: '/settings',
    name: 'settings.index',
    component: SettingsHomePage,
    beforeEnter: () => (canAccessSettings(useAuthStore()) ? true : { name: 'home.index' }),
    meta: {
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Настройки',
      navigationSection: 'settings',
      locationOptional: true,
    },
  },

  // Employees (GraphQL)
  {
    path: '/employees',
    name: 'employees.index',
    component: EmployeesPage,
    meta: {
      navigationSection: 'settings',
      requiresAuth: true,
      title: 'Сотрудники',
      permissions: ['employees.manage'],
    },
  },

  {
    path: '/business-settings/company',
    name: 'business-settings.company',
    component: BusinessSettingsPage,
    meta: {
      navigationSection: 'settings',
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Компания',
      locationOptional: true,
      permissions: COMPANY_SETTINGS_PERMISSIONS,
    },
  },
  {
    path: '/business-settings/locations',
    name: 'business-settings.locations',
    component: BusinessSettingsPage,
    meta: {
      navigationSection: 'settings',
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Локации',
      locationOptional: true,
      permissions: LOCATION_SETTINGS_PERMISSIONS,
    },
  },
  {
    path: '/business-settings/locations/:locationId/:section?',
    name: 'business-settings.locations.detail',
    component: BusinessSettingsPage,
    meta: {
      navigationSection: 'settings',
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Настройки локации',
      locationOptional: true,
      permissions: LOCATION_SETTINGS_PERMISSIONS,
    },
  },
  {
    path: '/location-settings',
    name: 'location-settings.index',
    redirect: workingLocationSection('hours'),
  },
  {
    path: '/client-settings/segments',
    name: 'client-settings.segments',
    component: ClientSegmentsPage,
    meta: clientSegmentsRouteMeta,
  },
  // Редактор — слой поверх того же списка: страница не пересоздаётся, Back/Forward закрывают и открывают его.
  {
    path: '/client-settings/segments/new',
    name: 'client-settings.segments.create',
    component: ClientSegmentsPage,
    meta: { ...clientSegmentsRouteMeta, title: 'Новый сегмент' },
  },
  {
    path: '/client-settings/segments/:id',
    name: 'client-settings.segments.edit',
    component: ClientSegmentsPage,
    meta: { ...clientSegmentsRouteMeta, title: 'Изменение сегмента' },
  },
  {
    path: '/loyalty-settings',
    name: 'loyalty-settings.index',
    component: LoyaltySettingsPage,
    meta: {
      navigationSection: 'settings',
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Программа лояльности',
      modules: [OrganizationModule.Loyalty],
      permissions: ['loyalty.manage'],
    },
  },
  {
    path: '/settings/services',
    name: 'settings.services',
    component: ServicesSettingsPage,
    meta: {
      navigationSection: 'settings',
      requiresAuth: true,
      requiresOrganization: true,
      title: 'Услуги',
      locationOptional: true,
      permissions: ['catalog.manage'],
    },
  },
  {
    path: '/settings/tire-service',
    name: 'settings.tire-service',
    redirect: { name: 'settings.tire-service.price' },
  },
  {
    path: '/settings/tire-service/price',
    name: 'settings.tire-service.price',
    component: TireServiceSettingsPage,
    meta: { ...tireServiceSettingsRouteMeta, title: 'Классы и диаметры' },
  },
  {
    path: '/settings/tire-service/warehouses',
    name: 'settings.tire-service.warehouses',
    component: TireServiceSettingsPage,
    meta: { ...tireServiceSettingsRouteMeta, title: 'Склады хранения' },
  },
  {
    path: '/settings/add-ons',
    name: 'settings.add-ons',
    component: AddOnsPage,
    meta: { ...addOnsRouteMeta, title: 'Дополнения' },
  },
  {
    path: '/settings/add-ons/robokassa',
    name: 'settings.add-ons.robokassa',
    component: RobokassaAddOnPage,
    meta: { ...addOnsRouteMeta, title: 'Robokassa' },
  },
  {
    path: '/settings/add-ons/tire-service',
    name: 'settings.add-ons.tire-service',
    component: TireServiceAddOnPage,
    meta: { ...addOnsRouteMeta, title: 'Шиномонтаж', permissions: [ADD_ONS_PERMISSION, 'tire_service.settings'] },
  },
  {
    path: '/settings/add-ons/invoices',
    name: 'settings.add-ons.invoices',
    component: InvoicesAddOnPage,
    meta: { ...addOnsRouteMeta, title: 'Оплата по счёту' },
  },
  {
    path: '/settings/add-ons/radio',
    name: 'settings.add-ons.radio',
    component: RadioAddOnPage,
    meta: { ...addOnsRouteMeta, title: 'Радио' },
  },
  {
    path: '/fiscalization-settings',
    name: 'fiscalization-settings.index',
    redirect: workingLocationSection('orders'),
  },
  // Переходный redirect: старый URL страницы (открытые вкладки/закладки)
  {
    path: '/booking-settings',
    redirect: workingLocationSection('hours'),
  },
];
